import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { Loader2, Eye, EyeOff } from 'lucide-react';
import { Logo } from '../../components/common/Logo';
import bg from '../../assets/bg-register.jpg';
import { confirmPasswordReset } from '../../services/Firebase/Login';
import { Capacitor } from '@capacitor/core';
import type { Auth } from 'firebase/auth';

export const ResetPasswordPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  // Get oobCode from URL query parameters (Firebase action code)
  const oobCode = searchParams.get('oobCode') || searchParams.get('oobcode');
  const mode = searchParams.get('mode');

  // Check if this is a valid password reset link
  useEffect(() => {
    if (!oobCode || mode !== 'resetPassword') {
      setError('Invalid or expired password reset link. Please request a new one.');
    }
  }, [oobCode, mode]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!oobCode) {
      setError('Invalid reset link. Please request a new password reset email.');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (newPassword.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // Get Firebase Auth instance for web
      let webAuth: Auth | undefined;
      if (!Capacitor.isNativePlatform()) {
        const firebaseModule = await import('../../config/firebase');
        webAuth = firebaseModule.auth || undefined;
      }

      // Confirm password reset using Firebase
      await confirmPasswordReset(oobCode, newPassword, webAuth);
      
      setSuccess(true);
      
      // Navigate to login after a short delay
      setTimeout(() => {
        navigate('/login', {
          state: { message: 'Password has been reset successfully. Please login with your new password.' }
        });
      }, 2000);
    } catch (err: any) {
      console.error('Password reset failed');
      
      // Handle specific Firebase error codes
      let errorMessage = 'Failed to reset password. Please try again.';
      
      if (err.code === 'auth/expired-action-code') {
        errorMessage = 'This password reset link has expired. Please request a new one.';
      } else if (err.code === 'auth/invalid-action-code') {
        errorMessage = 'This password reset link is invalid. Please request a new one.';
      } else if (err.code === 'auth/weak-password') {
        errorMessage = 'Password is too weak. Please choose a stronger password.';
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  if (!oobCode || mode !== 'resetPassword') {
    return (
      <div
        className="h-screen bg-cover bg-center bg-no-repeat"
        style={{ backgroundImage: `url(${bg})` }}
      >
        <div className="h-screen flex flex-col items-center justify-center px-4">
          <div className="w-full max-w-md space-y-8 text-center">
            <div className="flex justify-center mb-8">
              <div className="scale-150">
                <Logo />
              </div>
            </div>
            <div className="bg-red-500/10 p-4 rounded-lg">
              <p className="text-red-400">Invalid or expired password reset link. Please request a new one.</p>
              <Link to="/recover-password" className="text-[#5CD2F4] hover:underline mt-4 block">
                Request New Reset Link
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div
        className="h-screen bg-cover bg-center bg-no-repeat"
        style={{ backgroundImage: `url(${bg})` }}
      >
        <div className="h-screen flex flex-col items-center justify-center px-4">
          <div className="w-full max-w-md space-y-8 text-center">
            <div className="flex justify-center mb-8">
              <div className="scale-150">
                <Logo />
              </div>
            </div>
            <div className="bg-gradient-to-br from-[#79c9cb]/10 to-[#4b7bdd]/10 text-[#79c9cb] p-4 rounded-lg">
              <p className="font-semibold mb-2">Password reset successful!</p>
              <p className="text-sm">Redirecting to login...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="h-screen bg-cover bg-center bg-no-repeat"
      style={{ backgroundImage: `url(${bg})` }}
    >
      <div className="h-screen flex flex-col items-center justify-center px-4">
        <div className="w-full max-w-md space-y-8">
          {/* Logo section above titles */}
          <div className="flex justify-center mb-8">
            <div className="scale-150">
              <Logo />
            </div>
          </div>
          
          <div className="space-y-4 text-center">
            <div className="space-y-2">
              <h2 className="text-3xl sm:text-4xl text-white font-bold leading-snug tracking-tight text-center">
                Reset Your Password
              </h2>
              <p className="text-lg text-gray-300 font-montserrat font-medium">
                Enter your new password
              </p>
            </div>
          </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="space-y-4">
            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="New password"
                className="input-field pr-12"
                required
                disabled={isLoading}
                minLength={6}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-300"
              >
                {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>

            <div className="relative">
              <input
                type={showConfirmPassword ? "text" : "password"}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirm new password"
                className="input-field pr-12"
                required
                disabled={isLoading}
                minLength={6}
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-300"
              >
                {showConfirmPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>
          </div>
          
          {error && (
            <p className="text-red-500 text-sm text-center">{error}</p>
          )}
          
          <div className="space-y-3">
            <button
              type="submit"
              className="w-full px-4 py-3 bg-gradient-to-br from-[#79c9cb] to-[#4b7bdd] text-gray-900 rounded-lg font-semibold hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Resetting Password...
                </>
              ) : (
                'Reset Password'
              )}
            </button>
            
            <Link
              to="/recover-password"
              className="block text-center text-sm text-[#5CD2F4] hover:underline"
            >
              Request New Reset Link
            </Link>
          </div>
        </form>
      </div>
    </div>
    </div>
  );
};